/**
 * Two-factor authentication.
 *
 * The whole mechanism already existed on the server — enrol, confirm, disable,
 * recovery codes, and a sign-in flow that asks for a code when one is required.
 * There was simply no way to turn it on, so nobody had. This is that screen.
 *
 * Three deliberate choices:
 *
 * The secret is offered as text beside the QR code, not hidden behind "can't
 * scan?". Setting this up frequently means holding a phone in one hand and
 * looking at a laptop — but just as often the authenticator is *on* the device
 * showing this page, where there is no second camera and a QR code is useless.
 *
 * Enrolment is not finished until a code is entered. The server stores the
 * secret unenabled until then, so somebody who closes the tab half way is left
 * exactly as they were rather than locked out of their own account.
 *
 * And the recovery codes are shown once, on their own step, with copying and
 * downloading right there. They are the only way back in from a lost phone;
 * putting them in a toast that fades would be the single most damaging piece of
 * politeness in the product.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { useToast } from "../../components/ui/Toast.js";
import "./TwoFactorSection.css";

type Stage =
  | { at: "idle" }
  | { at: "enrolling"; uri: string; secret: string }
  | { at: "codes"; codes: string[] }
  | { at: "disabling" };

export function TwoFactorSection({
  enabled,
  onChanged,
}: {
  enabled: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const { toast } = useToast();
  const [stage, setStage] = useState<Stage>({ at: "idle" });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  const begin = useMutation({
    mutationFn: () => trpcClient.auth.totpBegin.mutate(),
    onSuccess: async (result) => {
      setError(null);
      setCode("");
      setStage({ at: "enrolling", uri: result.uri, secret: result.secret });
      setQr(await renderQr(result.uri));
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const confirm = useMutation({
    mutationFn: () => trpcClient.auth.totpConfirm.mutate({ code }),
    onSuccess: (result) => {
      setError(null);
      setCode("");
      setStage({ at: "codes", codes: result.recoveryCodes });
      onChanged();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const disable = useMutation({
    mutationFn: () => trpcClient.auth.totpDisable.mutate({ code }),
    onSuccess: () => {
      setError(null);
      setCode("");
      setStage({ at: "idle" });
      onChanged();
      toast(t("Two-factor authentication is off."));
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const busy = begin.isPending || confirm.isPending || disable.isPending;

  return (
    <section className="fc-account__section">
      <h2>{t("Two-factor authentication")}</h2>

      {error && (
        <p className="fc-account__error" role="alert">
          {error}
        </p>
      )}

      {/* ── Off, and not yet setting up ─────────────────────────────────── */}
      {!enabled && stage.at === "idle" && (
        <>
          <p className="fc-account__hint">
            {t(
              "An app on your phone generates a six-digit code that changes every thirty seconds. You will be asked for it when you sign in, and before anything destructive.",
            )}
          </p>
          <Button
            variant="secondary"
            loading={begin.isPending}
            onClick={() => begin.mutate()}
          >
            {t("Set up two-factor")}
          </Button>
        </>
      )}

      {/* ── Scanning, and proving it worked ─────────────────────────────── */}
      {stage.at === "enrolling" && (
        <div className="fc-2fa">
          <p className="fc-account__hint">
            {t(
              "Scan this with an authenticator app — Google Authenticator, 1Password, Aegis, whichever you use — then type the code it shows.",
            )}
          </p>

          <div className="fc-2fa__setup">
            {qr ? (
              <img
                className="fc-2fa__qr"
                src={qr}
                alt={t("QR code for your authenticator app")}
              />
            ) : (
              <div className="fc-2fa__qr fc-2fa__qr--pending" aria-hidden="true" />
            )}

            <div className="fc-2fa__manual">
              <p className="fc-account__hint">
                {t("Or type this key in by hand:")}
              </p>
              {/*
                Selectable and grouped in fours. Somebody typing 32 characters
                from a screen loses their place constantly; the spaces are not
                part of the key and every authenticator ignores them.
              */}
              <code className="fc-2fa__secret">{group4(stage.secret)}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void copy(stage.secret, toast, t)}
              >
                {t("Copy key")}
              </Button>
            </div>
          </div>

          <form
            className="fc-2fa__confirm"
            onSubmit={(event) => {
              event.preventDefault();
              confirm.mutate();
            }}
          >
            <Field label={t("Code from your app")} htmlFor="fc-totp-confirm">
              <Input
                id="fc-totp-confirm"
                value={code}
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, ""))
                }
              />
            </Field>

            <div className="fc-2fa__actions">
              <Button
                type="submit"
                variant="primary"
                loading={confirm.isPending}
                disabled={code.length !== 6 || busy}
              >
                {t("Turn it on")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setStage({ at: "idle" });
                  setError(null);
                }}
              >
                {t("Cancel")}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── The one time the recovery codes are ever shown ───────────────── */}
      {stage.at === "codes" && (
        <div className="fc-2fa">
          <p className="fc-account__hint fc-2fa__warning">
            {t(
              "Save these now. Each one signs you in once if you lose your phone, and this is the only time they are shown — we keep only hashes, so we cannot show them again.",
            )}
          </p>

          <ul className="fc-2fa__codes fc-tabular">
            {stage.codes.map((recovery) => (
              <li key={recovery}>{recovery}</li>
            ))}
          </ul>

          <div className="fc-2fa__actions">
            <Button
              variant="secondary"
              onClick={() => void copy(stage.codes.join("\n"), toast, t)}
            >
              {t("Copy all")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => saveCodes(stage.codes)}
            >
              {t("Download")}
            </Button>
            <Button variant="primary" onClick={() => setStage({ at: "idle" })}>
              {t("I have saved them")}
            </Button>
          </div>
        </div>
      )}

      {/* ── On ──────────────────────────────────────────────────────────── */}
      {enabled && stage.at === "idle" && (
        <>
          <p className="fc-account__hint">
            {t("Two-factor authentication is on for this account.")}
          </p>
          <Button variant="ghost" onClick={() => setStage({ at: "disabling" })}>
            {t("Turn it off")}
          </Button>
        </>
      )}

      {stage.at === "disabling" && (
        <form
          className="fc-2fa__confirm"
          onSubmit={(event) => {
            event.preventDefault();
            disable.mutate();
          }}
        >
          <p className="fc-account__hint">
            {/* A code, not a password: whoever is removing the second factor
                should have to hold the second factor. */}
            {t("Enter a current code to turn two-factor off.")}
          </p>

          <Field label={t("Code from your app")} htmlFor="fc-totp-disable">
            <Input
              id="fc-totp-disable"
              value={code}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, ""))
              }
            />
          </Field>

          <div className="fc-2fa__actions">
            <Button
              type="submit"
              variant="danger"
              loading={disable.isPending}
              disabled={code.length !== 6 || busy}
            >
              {t("Turn it off")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setStage({ at: "idle" });
                setCode("");
                setError(null);
              }}
            >
              {t("Keep it on")}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

/**
 * The QR code, as a data URL.
 *
 * Imported on demand: this is fifteen kilobytes of encoder that matters on one
 * screen, and loading it with the app would put it in front of every sign-in.
 *
 * Error correction level M and an automatic type number, which is what the
 * library picks for a URI of this length. The `otpauth://` URI is around a
 * hundred characters — comfortably inside what a phone camera reads at this
 * size.
 */
async function renderQr(uri: string): Promise<string | null> {
  try {
    const { default: qrcode } = await import("qrcode-generator");
    const qr = qrcode(0, "M");
    qr.addData(uri);
    qr.make();
    /* Cell size 4, quiet zone 4 — the margin is part of the spec, not padding. */
    return qr.createDataURL(4, 4);
  } catch {
    /* The key is on screen beside this; typing it in still works. */
    return null;
  }
}

/** Groups of four, so a 32-character key can be typed without losing your place. */
function group4(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

async function copy(
  text: string,
  toast: (message: string, options?: { variant?: "danger" }) => void,
  t: (key: string) => string,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(t("Copied."));
  } catch {
    toast(t("Couldn't copy — select the text and copy it manually."), {
      variant: "danger",
    });
  }
}

/** A text file, because a screenshot of recovery codes lives in a photo roll. */
function saveCodes(codes: string[]): void {
  const blob = new Blob(
    [
      "MyFriends.Cloud recovery codes\n",
      "Each code signs you in once if you lose your authenticator.\n",
      "Keep this somewhere only you can reach.\n\n",
      codes.join("\n"),
      "\n",
    ],
    { type: "text/plain" },
  );

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "myfriends-cloud-recovery-codes.txt";
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
